import {FC} from 'react'
import {InteractionType} from '@azure/msal-browser'
import type {AccountInfo} from '@azure/msal-browser'
import {useMsal} from '@azure/msal-react'
import authConfig from './authConfig'
import * as auth from '../redux/AuthRedux'
import {useDispatch} from 'react-redux'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {loginMicrosoftUser} from '../redux/AuthCRUD'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'

type Props = {
  setStatus: (status?: string) => void
  captchaEnabled: boolean
}

export const SignInButton: FC<Props> = ({setStatus, captchaEnabled}) => {
  const {instance} = useMsal()
  const dispatch = useDispatch()
  const {executeRecaptcha} = useGoogleReCaptcha()

  const handleLogin = () => {
    if (authConfig.loginMode === InteractionType.Redirect) {
      instance.loginRedirect({scopes: authConfig.api.scopes}).catch((error: any) => {
        console.error('MSAL login error:', error)
        setStatus('Microsoft sign-in failed.')
      })
    } else {
      loginPopup()
    }
  }

  const loginPopup = async () => {
    try {
      const loginResponse = await instance.loginPopup({scopes: authConfig.api.scopes})
      const account = loginResponse.account
      if (!account) {
        setStatus('Unable to complete Microsoft login.')
        return
      }

      instance.setActiveAccount(account)
      const claims = loginResponse.idTokenClaims as Record<string, any> | undefined
      const oid = claims?.oid ?? claims?.objectId ?? null
      const upn = claims?.upn ?? claims?.preferred_username ?? account.username ?? null
      const microsoftBearerToken =
        loginResponse.accessToken ||
        (await getMicrosoftBearerToken(account, loginResponse.idToken))

      if (!microsoftBearerToken) {
        setStatus('Unable to complete Microsoft login.')
        return
      }

      await authenticateWithApi(account.username ?? '', oid, upn, microsoftBearerToken)
    } catch (error: any) {
      console.error('Microsoft sign-in popup failed:', error)
      setStatus('Microsoft sign-in failed.')
    }
  }

  const getMicrosoftBearerToken = async (account: AccountInfo, idToken?: string) => {
    if (authConfig.api.scopes.length === 0) {
      return idToken ?? ''
    }

    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: authConfig.api.scopes,
        account,
      })
      return tokenResponse.accessToken || idToken || ''
    } catch (silentError) {
      console.error('Microsoft API token silent acquisition failed:', silentError)
    }

    try {
      const tokenResponse = await instance.acquireTokenPopup({
        scopes: authConfig.api.scopes,
        account,
      })
      return tokenResponse.accessToken || idToken || ''
    } catch (popupError) {
      console.error('Microsoft API token popup acquisition failed:', popupError)
      return idToken ?? ''
    }
  }

  const authenticateWithApi = async (
    email: string,
    oid?: string | null,
    upn?: string | null,
    microsoftBearerToken?: string | null
  ) => {
    try {
      setStatus(undefined)
      let captchaToken: string | null = null

      if (captchaEnabled) {
        if (!executeRecaptcha) {
          setStatus('Captcha is not ready. Please refresh and try again.')
          return
        }
        captchaToken = await executeRecaptcha('login')
      }

      const {data} = await loginMicrosoftUser(email, captchaToken, true, oid, upn, microsoftBearerToken)
      const {
        token,
        refreshToken,
        longTermToken,
        name,
      } = data ?? {}

      const isRememberMe = true

      if (!token && !longTermToken) {
        setStatus('Unable to complete Microsoft login.')
        return
      }

      const effectiveAccessToken = longTermToken ?? token ?? ''

      dispatch(
        auth.actions.login(
          effectiveAccessToken,
          refreshToken ?? '',
          isRememberMe,
          longTermToken,
          name ?? email
        )
      )
    } catch (error: any) {
      console.error('Microsoft login error:', error)
      if (error?.response?.data?.message) {
        setStatus(error.response.data.message)
      } else {
        setStatus('Unable to complete Microsoft login.')
      }
    }
  }

  return (
    <button
      className='btn btn-outline-secondary d-flex align-items-center justify-content-center gap-3 w-100 py-3'
      type='button'
      onClick={handleLogin}
    >
      <img
        src={toAbsoluteUrl('/media/icons/Microsoft.svg')}
        alt='Microsoft'
        style={{height: 20}}
      />
      <span className='fw-semibold text-dark'>Log in with Microsoft</span>
    </button>
  )
}


